import { loadScript } from '@guardian/libs';
import { getViewId } from '@guardian/ophan-tracker-js';
import { BillingPeriod } from '@modules/product/billingPeriod';
import { logException } from 'helpers/utilities/logger';
import { getMvtId } from '../abTests/mvt';
import { canRunQuantumMetric, getContributionAnnualValue, getConvertedAnnualValue, getConvertedValue, waitForQuantumMetricAPi, } from './quantumMetricHelpers';
var SendEventAcquisitionDataFromQueryParam;
(function (SendEventAcquisitionDataFromQueryParam) {
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["Source"] = 94] = "Source";
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["ComponentId"] = 95] = "ComponentId";
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["ComponentType"] = 96] = "ComponentType";
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["CampaignCode"] = 97] = "CampaignCode";
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["ReferrerUrl"] = 99] = "ReferrerUrl";
    SendEventAcquisitionDataFromQueryParam[SendEventAcquisitionDataFromQueryParam["IsRemote"] = 100] = "IsRemote";
})(SendEventAcquisitionDataFromQueryParam || (SendEventAcquisitionDataFromQueryParam = {}));
var SendEventSubscriptionCheckoutStart;
(function (SendEventSubscriptionCheckoutStart) {
    SendEventSubscriptionCheckoutStart[SendEventSubscriptionCheckoutStart["DigiSub"] = 75] = "DigiSub";
    SendEventSubscriptionCheckoutStart[SendEventSubscriptionCheckoutStart["PaperSub"] = 76] = "PaperSub";
    SendEventSubscriptionCheckoutStart[SendEventSubscriptionCheckoutStart["GuardianWeeklySub"] = 77] = "GuardianWeeklySub";
    SendEventSubscriptionCheckoutStart[SendEventSubscriptionCheckoutStart["GuardianWeeklySubGift"] = 79] = "GuardianWeeklySubGift";
})(SendEventSubscriptionCheckoutStart || (SendEventSubscriptionCheckoutStart = {}));
var SendEventSubscriptionCheckoutConversion;
(function (SendEventSubscriptionCheckoutConversion) {
    SendEventSubscriptionCheckoutConversion[SendEventSubscriptionCheckoutConversion["DigiSub"] = 31] = "DigiSub";
    SendEventSubscriptionCheckoutConversion[SendEventSubscriptionCheckoutConversion["PaperSub"] = 67] = "PaperSub";
    SendEventSubscriptionCheckoutConversion[SendEventSubscriptionCheckoutConversion["GuardianWeeklySub"] = 68] = "GuardianWeeklySub";
    SendEventSubscriptionCheckoutConversion[SendEventSubscriptionCheckoutConversion["GuardianWeeklySubGift"] = 70] = "GuardianWeeklySubGift";
})(SendEventSubscriptionCheckoutConversion || (SendEventSubscriptionCheckoutConversion = {}));
var SendEventContributionAmountUpdate;
(function (SendEventContributionAmountUpdate) {
    SendEventContributionAmountUpdate[SendEventContributionAmountUpdate["SingleContribution"] = 71] = "SingleContribution";
    SendEventContributionAmountUpdate[SendEventContributionAmountUpdate["RecurringContribution"] = 72] = "RecurringContribution";
})(SendEventContributionAmountUpdate || (SendEventContributionAmountUpdate = {}));
var SendEventContributionPaymentMethodUpdate;
(function (SendEventContributionPaymentMethodUpdate) {
    SendEventContributionPaymentMethodUpdate[SendEventContributionPaymentMethodUpdate["PaymentMethod"] = 103] = "PaymentMethod";
    SendEventContributionPaymentMethodUpdate[SendEventContributionPaymentMethodUpdate["PaymentMethodAtConversion"] = 110] = "PaymentMethodAtConversion";
})(SendEventContributionPaymentMethodUpdate || (SendEventContributionPaymentMethodUpdate = {}));
var SendEventContributionCheckoutConversion;
(function (SendEventContributionCheckoutConversion) {
    SendEventContributionCheckoutConversion[SendEventContributionCheckoutConversion["SingleContribution"] = 73] = "SingleContribution";
    SendEventContributionCheckoutConversion[SendEventContributionCheckoutConversion["RecurringContribution"] = 74] = "RecurringContribution";
})(SendEventContributionCheckoutConversion || (SendEventContributionCheckoutConversion = {}));
// ---- sendEvent logic ---- //
const { DigiSub, PaperSub, GuardianWeeklySub, GuardianWeeklySubGift } = SendEventSubscriptionCheckoutStart;
const { SingleContribution, RecurringContribution } = SendEventContributionAmountUpdate;
const cartValueEventIds = [
    DigiSub,
    PaperSub,
    GuardianWeeklySub,
    GuardianWeeklySubGift,
    SingleContribution,
    RecurringContribution,
];
// We only want to send a sample percentage of our user traffic to Quantum Metric.
function userIsInSampledCohort() {
    const percentageOfUsersToSample = 10;
    const divisor = 100 / percentageOfUsersToSample;
    const mvtId = getMvtId();
    return mvtId % divisor === 0;
}
async function ifQmPermitted(callback) {
    const canRun = await canRunQuantumMetric();
    if (canRun && userIsInSampledCohort()) {
        callback();
    }
}
function sendEvent(id, isConversion, value, payload) {
    /**
     * A cart value event is indicated by 64 in QM.
     * A non cart value event is indicated by 0 in QM.
     * And a conversion event is indicated by 1 in QM.
     */
    const qmCartValueEventId = isConversion
        ? 1
        : cartValueEventIds.includes(id)
            ? 64
            : 0;
    if (window.QuantumMetricAPI?.isOn()) {
        window.QuantumMetricAPI.sendEvent(id, qmCartValueEventId, value, payload ?? {});
    }
}
function sendEventWhenReadyTrigger(sendEventWhenReady) {
    /**
     * Quantum Metric's script sets up QuantumMetricAPI.
     * We need to check it is defined and ready before we can
     * send events to it. If it is ready we call sendEventWhenReady
     * immediately. If it is not ready we poll a function that checks
     * if QuantumMetricAPI is available. Once it's available we
     * call sendEventWhenReady.
     */
    if (window.QuantumMetricAPI?.isOn()) {
        sendEventWhenReady();
    }
    else {
        waitForQuantumMetricAPi(() => {
            sendEventWhenReady();
        });
    }
}
function sendEventAcquisitionDataFromQueryParamEvent(acquisitionData) {
    void ifQmPermitted(() => {
        const sendEventWhenReady = () => {
            const acquisitionDataKeysToLog = {
                source: SendEventAcquisitionDataFromQueryParam.Source,
                componentId: SendEventAcquisitionDataFromQueryParam.ComponentId,
                componentType: SendEventAcquisitionDataFromQueryParam.ComponentType,
                campaignCode: SendEventAcquisitionDataFromQueryParam.CampaignCode,
                referrerUrl: SendEventAcquisitionDataFromQueryParam.ReferrerUrl,
                isRemote: SendEventAcquisitionDataFromQueryParam.IsRemote,
            };
            Object.keys(acquisitionDataKeysToLog).forEach((key) => {
                const acquisitionDataValueToLog = JSON.stringify(acquisitionData[key]);
                if (acquisitionDataValueToLog && acquisitionDataKeysToLog[key]) {
                    sendEvent(acquisitionDataKeysToLog[key] ?? 0, false, acquisitionDataValueToLog.toString());
                }
            });
        };
        sendEventWhenReadyTrigger(sendEventWhenReady);
    });
}
function sendEventOneTimeCheckoutValue(amount, sourceCurrency, isConversion) {
    void ifQmPermitted(() => {
        const sendEventWhenReady = () => {
            const sendEventId = isConversion ? 183 : 182;
            const convertedValue = getConvertedValue(amount, sourceCurrency);
            const payload = {
                product: 'ONE-OFF',
            };
            if (convertedValue) {
                sendEvent(sendEventId, !!isConversion, Math.round(convertedValue).toString(), payload);
            }
        };
        sendEventWhenReadyTrigger(sendEventWhenReady);
    });
}
function sendEventCheckoutValue(amount, product, billingPeriod, sourceCurrency, isConversion) {
    void ifQmPermitted(() => {
        const sendEventWhenReady = () => {
            const sendEventId = isConversion ? 183 : 182;
            const convertedValue = getConvertedAnnualValue(billingPeriod, amount, sourceCurrency);
            const payload = {
                product,
                billingPeriod,
            };
            if (convertedValue) {
                sendEvent(sendEventId, !!isConversion, Math.round(convertedValue).toString(), payload);
            }
        };
        sendEventWhenReadyTrigger(sendEventWhenReady);
    });
}
// TODO: To be deleted with the 2-step checkout
function sendEventContributionCheckoutConversion(amount, billingPeriod, sourceCurrency) {
    void ifQmPermitted(() => {
        const sendEventWhenReady = () => {
            const sendEventId = billingPeriod === BillingPeriod.OneTime
                ? SendEventContributionCheckoutConversion.SingleContribution
                : SendEventContributionCheckoutConversion.RecurringContribution;
            const convertedValue = getContributionAnnualValue(billingPeriod, amount, sourceCurrency);
            if (convertedValue) {
                sendEvent(sendEventId, true, Math.round(convertedValue).toString());
            }
        };
        sendEventWhenReadyTrigger(sendEventWhenReady);
    });
}
function sendEventPaymentMethodSelected(paymentMethod) {
    if (paymentMethod) {
        void ifQmPermitted(() => {
            const sendEventWhenReady = () => {
                const sendEventId = SendEventContributionPaymentMethodUpdate.PaymentMethod;
                sendEvent(sendEventId, false, paymentMethod.toString());
            };
            sendEventWhenReadyTrigger(sendEventWhenReady);
        });
    }
}
function sendEventABTestParticipations(participations) {
    const sendEventABTestId = 30;
    const valueQueue = [];
    Object.keys(participations).forEach((testId) => {
        const value = `${testId}-${participations[testId]}`;
        /**
         * Quantum Metric's script sets up QuantumMetricAPI
         * We need to check it is defined and ready before we can
         * send events to it. If it is not ready we add the events to
         * a valueQueue to be processed later.
         */
        if (window.QuantumMetricAPI?.isOn()) {
            sendEvent(sendEventABTestId, false, value);
        }
        else {
            valueQueue.push(value);
        }
    });
    /**
     * If valueQueue is populated QuantumMetricAPI was not ready to be
     * sent events, in this scenario we poll a function that checks if
     * QuantumMetricAPI is available. Once it's available we process the
     * queue of values to be sent with sendEvent.
     */
    if (valueQueue.length) {
        waitForQuantumMetricAPi(() => {
            valueQueue.forEach((value) => {
                sendEvent(sendEventABTestId, false, value);
            });
        });
    }
}
function sendEventPageViewId() {
    const sendEventPageViewId = 181;
    void ifQmPermitted(() => {
        const sendEventWhenReady = () => {
            sendEvent(sendEventPageViewId, false, getViewId());
        };
        sendEventWhenReadyTrigger(sendEventWhenReady);
    });
}
// ---- initialisation logic ---- //
function addQM() {
    return loadScript('https://cdn.quantummetric.com/instrumentation/1.35.4/quantum-gnm.js', {
        async: true,
        integrity: 'sha384-VMLIC70VzACtZAEkPaL+7xW+v0+UjkIUuGxlArtIG+Pzqlp5DkbfVG9tRm75Liwx',
        crossOrigin: 'anonymous',
    }).catch(() => {
        logException('Failed to load Quantum Metric');
    });
}
function init(participations, acquisitionData) {
    void ifQmPermitted(() => {
        void addQM().then(() => {
            /**
             * Quantum Metric's script has loaded so we can attempt to
             * send user AB test participations, acquisition data and
             * the current page view ID via the sendEvent function.
             */
            sendEventABTestParticipations(participations);
            sendEventAcquisitionDataFromQueryParamEvent(acquisitionData);
            sendEventPageViewId();
        });
    });
}
// ----- Exports ----- //
export { init, sendEventContributionCheckoutConversion, sendEventPaymentMethodSelected, sendEventCheckoutValue, sendEventOneTimeCheckoutValue, };
